/** The pages made from the data (api/catalog.py): a model, a cloud, a GPU, a country, and the lists that lead to
 *  them. Each is one answer from /api/pages/<path>, drawn here; the server writes the same figures into the HTML
 *  for readers that run no scripts. */
import { useEffect, useMemo, useState } from 'react'
import { BASE, api } from '../api'
import type { FamilyCard, FamilyRow, ChangePageData, CloudPageData, CountryPageData, ExplorePageData, GpuPageData, ModelPageData, ReportPageData, VendorWhere } from '../types'
import { useSettings } from '../settings'
import { ErrorNote, Tag } from './ui'
import { useAsync } from './useAsync'
import { useCountry } from './geoDetect'

type Go = (path: string) => void
type OpenTool = (tool: 'calculator' | 'compare' | 'fit', canonical: string) => void

const usd = (v: number | null | undefined) => {
  if (v == null) return '—'
  if (v >= 1) return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const [whole, cents = ''] = v.toFixed(4).replace(/0+$/, '').split('.')
  return `$${whole}.${cents.padEnd(2, '0')}`
}
const day = (ts: number | null | undefined) => (ts ? new Date(ts * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : '')

/** A link inside the site: the address is real (a crawler, or a new tab, follows it), a plain click stays in the app. */
function A({ to, go, children, className }: { to: string | null; go: Go; children: React.ReactNode; className?: string }) {
  const live = useSettings().explore?.live
  // a kind of page the administrator switched off, or an item with no page of its own: the words, without the link
  if (!to || (live && !live.some((p) => to === p || (p.endsWith('/') && to.startsWith(p))))) return <span className={className}>{children}</span>
  return (
    <a className={className} href={`${BASE}${to}`}
      onClick={(e) => { if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) { e.preventDefault(); go(to) } }}>
      {children}
    </a>
  )
}

const Crumbs = ({ go, trail }: { go: Go; trail: [string, string][] }) => (
  <p className="crumbs small">{trail.map(([to, label], i) => <span key={to}>{i ? ' › ' : ''}<A to={to} go={go}>{label}</A></span>)}</p>
)
const Collected = ({ at }: { at: number | null | undefined }) => (
  at ? <p className="muted small collected">List prices collected {day(at)}, refreshed every three hours. No negotiated rates or taxes.</p> : null
)
const Tile = ({ label, value, foot, win }: { label: string; value: string; foot?: string; win?: boolean }) => (
  <div className={win ? 'stat win' : 'stat'}><div className="label">{label}</div><div className="value">{value}</div>{foot ? <div className="foot">{foot}</div> : null}</div>
)

function Model({ d, go, openTool }: { d: ModelPageData; go: Go; openTool: OpenTool }) {
  const f = d.facts
  const sold = d.tokens.filter((r) => r.sold)
  const best = sold[0]
  const hw = d.hardware[0]
  const missing = d.tokens.filter((r) => !r.sold).map((r) => r.cloud)
  const b = d.breakeven
  return (
    <>
      <section className="panel explore">
        <Crumbs go={go} trail={[['models', 'Models']]} />
        <h2 className="pagehead">{d.label}: {d.priced ? 'price on every cloud' : 'GPU memory and hosting cost'}</h2>
        <div className="chips">
          {d.open_weights ? <span className="chip on">Open weights</span> : <span className="chip">Closed weights</span>}
          {f.params_b ? <span className="chip">{f.params_b.toLocaleString()} B parameters</span> : null}
          {f.architecture ? <span className="chip">{f.architecture}</span> : null}
          {f.context ? <span className="chip">{f.context.toLocaleString()}-token context</span> : null}
          {f.license ? <span className="chip">{f.license}</span> : null}
          {d.maker ? <span className="chip">{d.maker}</span> : null}
        </div>
        <div className="grid3 trafficstats">
          {d.priced ? (
            <>
              <Tile win label="Cheapest per token" value={best ? `${usd(best.input)} / ${usd(best.output)}` : '—'} foot={best ? `in / out per 1M · ${best.cloud}` : 'not sold per token'} />
              <Tile label="Sold per token by" value={`${d.sold_by} of ${d.clouds} clouds`} foot={missing.length ? `not ${missing.join(', ')}` : 'every cloud compared'} />
            </>
          ) : <Tile win label="Sold per token" value="By no cloud" foot="you run it yourself" />}
          {d.required_vram_gb ? <Tile label="GPU memory to run it" value={`${d.required_vram_gb.toLocaleString()} GB`} foot={f.precision === 'published size' ? 'sized from the weight files it publishes' : f.precision ? `at ${f.precision}, as shipped` : undefined} /> : null}
          {hw ? <Tile label="Cheapest to self-host" value={`${usd(hw.usd_per_hour)} an hour`} foot={`${hw.cloud}, ${hw.gpu_count} × ${hw.gpu_model} ${hw.gpu_mem_gb} GB`} /> : null}
        </div>
      </section>

      {d.priced ? (
        <section className="panel explore">
          <h2>Per token, by cloud</h2>
          <div className="tablewrap">
            <table className="plain">
              <thead><tr><th>Cloud</th><th>Input, per 1M</th><th>Output, per 1M</th><th>Cheapest region</th></tr></thead>
              <tbody>
                {d.tokens.map((r, i) => (
                  <tr key={r.provider} className={r.sold ? undefined : 'off'}>
                    <td><A to={`vendors/${r.provider}`} go={go}>{r.cloud}</A>{r.sold && i === 0 && sold.length > 1 ? <> <Tag>cheapest</Tag></> : null}</td>
                    <td>{r.sold ? usd(r.input) : '—'}</td><td>{r.sold ? usd(r.output) : '—'}</td>
                    <td>{r.sold ? <>{r.region}{r.regions > 1 ? ` · ${r.regions} regions` : ''}</> : <span className="muted">not sold per token</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {d.required_vram_gb ? (
        <section className="panel explore">
          <h2>Run it yourself: the cheapest instance that fits, by cloud</h2>
          {d.hardware.length ? (
            <div className="tablewrap">
              <table className="plain">
                <thead><tr><th>Cloud</th><th>Instance</th><th>GPUs</th><th>An hour</th><th>A month, 24×7</th></tr></thead>
                <tbody>
                  {d.hardware.map((h) => (
                    <tr key={h.provider}>
                      <td><A to={`vendors/${h.provider}`} go={go}>{h.cloud}</A></td><td>{h.servers ? <><b>{h.servers} ×</b> </> : null}<code>{h.instance}</code>{h.servers ? <span className="muted small"> joined by {h.cluster_network}</span> : null}</td>
                      <td>{h.gpu_count} × {h.gpu_model} {h.gpu_mem_gb} GB{h.servers ? ' in each' : ''}</td><td>{usd(h.usd_per_hour)}</td><td>${h.usd_per_month.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="muted small">No priced instance holds {d.required_vram_gb.toLocaleString()} GB, on one machine or across several.</p>}
          {d.hardware.some((h) => h.servers) ? (
            <p className="muted small" style={{ marginTop: 8 }}>Several servers: the model is sized across identical servers that hold it together, on instance
              types whose vendor documents a network between servers; the price is all of them. It is shown where no one server holds
              the model, or where it costs less than the one that does.</p>
          ) : null}
          {b && b.achievable ? (
            <p className="small" style={{ marginTop: 10 }}>
              At {usd(best?.input)} / {usd(best?.output)} per million tokens, running it on {b.cloud} <code>{b.instance}</code> pays
              off above roughly {(b.tokens_per_month / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 })} billion tokens a month
              ({b.input_output_ratio} input tokens for each output token).
            </p>
          ) : null}
        </section>
      ) : null}

      <div className="row explorebtns">
        {d.priced ? <button type="button" className="pill action" onClick={() => openTool('calculator', d.canonical)}>Work out my monthly cost</button> : null}
        {d.priced ? <button type="button" className="pill" onClick={() => openTool('compare', d.canonical)}>Compare in detail</button> : null}
        {d.open_weights ? <button type="button" className="pill" onClick={() => openTool('fit', d.canonical)}>See all hardware that fits</button> : null}
      </div>

      <div className="explorerel">
        {d.related.maker.length ? (
          <div className="relcard"><h3>More from {d.maker}</h3><p>{d.related.maker.map((m, i) => <span key={m.slug}>{i ? ' · ' : ''}<A to={`models/${m.slug}`} go={go}>{m.label}</A></span>)}</p></div>
        ) : null}
        {d.related.size.length ? (
          <div className="relcard"><h3>Similar size, open weights</h3><p>{d.related.size.map((m, i) => <span key={m.slug}>{i ? ' · ' : ''}<A to={`models/${m.slug}`} go={go}>{m.label}</A></span>)}</p></div>
        ) : null}
        {d.related.country ? (
          <div className="relcard"><h3>Inside {d.related.country.name}</h3>
            <p><A to={`in-country/${d.related.country.slug}`} go={go}>{d.related.country.clouds.length ? `Sold in-country by ${d.related.country.clouds.join(', ')}` : 'No cloud sells it in-country'}</A></p></div>
        ) : null}
      </div>
      <Collected at={d.collected_at} />
    </>
  )
}

/** A family's open / closed tag: what a reader can do with its weights */
const weightsTag = (f: FamilyCard) => (f.closed && f.open ? 'open and closed' : f.closed ? 'closed' : 'open weights')

/** The Models page (2026-10-17, the owner's call after a mockup): By family first, the families A-Z as cards with
 *  their maker's logo; All models as one table. Nothing on it is ranked by maker. */
function Models({ d, go }: { d: Extract<ExplorePageData, { kind: 'models' }>; go: Go }) {
  const families = d.families ?? []
  const [view, setView] = useState<'family' | 'all'>(families.length ? 'family' : 'all')
  const [q, setQ] = useState('')
  const [only, setOnly] = useState(false)
  const [allFamilies, setAllFamilies] = useState(false)
  const [sort, setSort] = useState<'name' | 'maker' | 'size' | 'clouds'>('name')
  const needle = q.trim().toLowerCase()
  const shown = useMemo(() => d.models.filter((m) => (!only || m.priced)
    && (!needle || `${m.label} ${m.maker} ${m.slug}`.toLowerCase().includes(needle))), [d.models, needle, only])
  const sorted = useMemo(() => [...shown].sort((a, b) => sort === 'maker' ? (a.maker || '~').localeCompare(b.maker || '~') || a.label.localeCompare(b.label)
    : sort === 'size' ? (b.params_b ?? -1) - (a.params_b ?? -1) : sort === 'clouds' ? b.clouds - a.clouds || a.label.localeCompare(b.label)
    : a.label.localeCompare(b.label)), [shown, sort])
  const fams = families.filter((f) => (!only || f.priced) && (!needle || f.name.toLowerCase().includes(needle)
    || d.models.some((m) => m.maker === f.name && m.label.toLowerCase().includes(needle))))
  const main = fams.filter((f) => needle || allFamilies || f.priced || f.models >= 10)
  const folded = fams.length - main.length
  const priced = d.models.filter((m) => m.priced).length
  const LIMIT = 300
  return (
    <section className="panel explore">
      <h2 className="pagehead">Language models: prices and hardware for each</h2>
      <p className="muted">{d.models.length.toLocaleString()} models{families.length ? ` from ${families.length} families` : ''}. {priced} are sold per token by at least one vendor; every open model shows the GPU memory it needs.</p>
      <div className="ctybar">
        <input type="search" value={q} placeholder={view === 'family' ? 'Find a model or a family' : 'Find a model'} aria-label="Find a model or a family" onChange={(e) => setQ(e.target.value)} />
        {families.length ? (
          <span className="seg" role="group" aria-label="View">
            <button type="button" aria-pressed={view === 'family'} onClick={() => setView('family')}>By family</button>
            <button type="button" aria-pressed={view === 'all'} onClick={() => setView('all')}>All models</button>
          </span>
        ) : null}
        <label className="switchline small"><input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} /> only sold per token</label>
      </div>
      {view === 'family' ? (
        <>
          <div className="vndcards" style={{ marginTop: 12 }}>
            {main.map((f) => (
              <A key={f.slug} to={`models/family/${f.slug}`} go={go} className="vndcard famcard">
                <Logo src={f.logo} label={f.name} size={36} />
                <span className="vndcardbody">
                  <b>{f.name}</b>
                  <span className="muted small">{f.models} {f.models === 1 ? 'model' : 'models'} · {f.priced} sold per token</span>
                  <span className="vregchip">{weightsTag(f)}</span>
                </span>
              </A>
            ))}
          </div>
          {folded > 0 ? <p className="muted small" style={{ marginTop: 10 }}>+ {folded} smaller {folded === 1 ? 'family' : 'families'} ·{' '}
            <button type="button" className="linklike" onClick={() => setAllFamilies(true)}>Show all</button></p> : null}
          {!fams.length ? <p className="muted">No family or model matches “{q}”.</p> : null}
          <p className="muted small" style={{ marginTop: 10 }}>Families are in alphabetical order. Models filed under no family are under All models.</p>
        </>
      ) : (
        <>
          <div className="tablewrap" style={{ marginTop: 12 }}>
            <table className="plain modeltable">
              <thead><tr>
                {([['name', 'Model'], ['maker', 'Family'], ['size', 'Size'], ['clouds', 'Vendors per token']] as const).map(([k, label]) => (
                  <th key={k} className={k === 'clouds' || k === 'size' ? 'num' : undefined}>
                    <button type="button" className="linklike" aria-pressed={sort === k} onClick={() => setSort(k)}>{label}{sort === k ? ' ▾' : ''}</button>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {sorted.slice(0, LIMIT).map((m) => (
                  <tr key={m.slug}>
                    <td><A to={`models/${m.slug}`} go={go}>{m.label}</A></td>
                    <td>{m.maker && m.maker !== 'Other' && families.some((f) => f.name === m.maker)
                      ? <A to={`models/family/${families.find((f) => f.name === m.maker)!.slug}`} go={go}>{m.maker}</A> : <span className="muted">{m.maker || '—'}</span>}</td>
                    <td className="num">{m.params_b ? `${m.params_b.toLocaleString()}B` : m.open_weights ? '—' : <span className="muted">closed</span>}</td>
                    <td className="num">{m.clouds || <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small" style={{ marginTop: 8 }}>{shown.length.toLocaleString()} shown{shown.length > LIMIT ? `; the first ${LIMIT} are listed, type to narrow` : ''}.</p>
        </>
      )}
    </section>
  )
}

const money = (v: number | null) => v == null ? '—' : v >= 1 ? `$${v.toFixed(2)}` : `$${v.toFixed(v >= 0.1 ? 2 : 3)}`

/** A family's own page: its facts, then each model with who sells it per token, the cheapest pair and the memory */
function Family({ d, go }: { d: Extract<ExplorePageData, { kind: 'family' }>; go: Go }) {
  const [order, setOrder] = useState<'az' | 'size' | 'cheapest'>('az')
  const [only, setOnly] = useState(false)
  const rows = useMemo(() => {
    const list = d.rows.filter((r: FamilyRow) => !only || r.priced)
    if (order === 'size') return [...list].sort((a, b) => (b.params_b ?? -1) - (a.params_b ?? -1))
    if (order === 'cheapest') return [...list].sort((a, b) => ((a.input ?? Infinity) * 3 + (a.output ?? 0)) - ((b.input ?? Infinity) * 3 + (b.output ?? 0)))
    return list
  }, [d.rows, order, only])
  return (
    <section className="panel explore">
      <Crumbs go={go} trail={[['models', 'Models']]} />
      <div className="vhead">
        <Logo src={d.logo} label={d.name} size={48} />
        <div className="vtitle"><h2 className="pagehead">{d.name} models: prices and the hardware each needs</h2>
          <span className="muted small">{weightsTag(d)}</span></div>
      </div>
      <div className="famfacts">
        <div><span>Models</span><b>{d.models}</b></div>
        <div><span>Sold per token</span><b>{d.priced}</b></div>
        <div><span>Vendors selling one</span><b>{d.vendors}</b></div>
        <div><span>Smallest GPU memory</span><b>{d.smallest_vram_gb != null ? `${d.smallest_vram_gb.toLocaleString()} GB` : '—'}</b></div>
      </div>
      <div className="ctybar">
        <span className="seg" role="group" aria-label="Order">
          <button type="button" aria-pressed={order === 'az'} onClick={() => setOrder('az')}>A–Z</button>
          <button type="button" aria-pressed={order === 'size'} onClick={() => setOrder('size')}>By size</button>
          <button type="button" aria-pressed={order === 'cheapest'} onClick={() => setOrder('cheapest')}>Cheapest per token</button>
        </span>
        <label className="switchline small"><input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} /> only sold per token</label>
      </div>
      <div className="tablewrap">
        <table className="plain modeltable">
          <thead><tr><th>Model</th><th className="num">Size</th><th>Sold per token by</th><th className="num">From, in /1M</th><th className="num">Out /1M</th><th className="num">GPU memory</th></tr></thead>
          <tbody>
            {rows.map((r: FamilyRow) => (
              <tr key={r.slug}>
                <td><A to={`models/${r.slug}`} go={go}>{r.label}</A></td>
                <td className="num">{r.params_b ? `${r.params_b.toLocaleString()}B` : <span className="muted">{r.open_weights ? '—' : 'closed'}</span>}</td>
                <td className="small">{r.vendors.length ? r.vendors.join(' · ') : <span className="muted">not sold per token</span>}</td>
                <td className="num" title={r.cheapest ? `cheapest: ${r.cheapest}` : undefined}>{money(r.input)}</td>
                <td className="num">{money(r.output)}</td>
                <td className="num">{r.vram_gb != null ? `${r.vram_gb.toLocaleString()} GB` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small" style={{ marginTop: 8 }}>Vendors in alphabetical order. "From" is the cheapest list price among them, at each vendor's cheapest region;
        the model's page shows every vendor. GPU memory is the weights at the precision the model ships in, plus the usual 25% for the attention cache.</p>
    </section>
  )
}

const SELLS: Record<string, string> = { on_demand: 'per token', self_hosted: 'GPUs by the hour', dedicated: 'dedicated clusters' }

/** A vendor's mark, from the site's own files (no request leaves the site); its initials where there is none. */
function Logo({ src, label, size }: { src?: string | null; label: string; size: number }) {
  return src
    ? <img className="vlogo" src={`${BASE}${src}`} alt={`${label} logo`} width={size} height={size} />
    : <span className="vlogo mono" style={{ width: size, height: size }} aria-hidden="true">{label.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase()}</span>
}

function Clouds({ d, go }: { d: Extract<ExplorePageData, { kind: 'clouds' }>; go: Go }) {
  return (
    <section className="panel explore">
      <h2 className="pagehead">LLM hosting by vendor</h2>
      <p className="muted">Every vendor compared: who it is, what it sells for running language models, and where it runs.</p>
      <div className="vndcards">
        {d.clouds.map((c) => (
          <A key={c.slug} to={`vendors/${c.slug}`} go={go} className="vndcard">
            <Logo src={c.logo} label={c.label} size={32} />
            <span className="vndcardbody">
              <b>{c.label}</b>
              <span className="muted small">{[...(c.kind ?? []).slice(0, 1), ...(c.sells ?? []).map((m) => SELLS[m] ?? m)].join(' · ')}</span>
              <span className="vregchip">{c.countries ? `${c.regions} ${c.regions === 1 ? 'region' : 'regions'} · ${c.countries} ${c.countries === 1 ? 'country' : 'countries'}` : 'One price for every region'}</span>
            </span>
          </A>
        ))}
      </div>
    </section>
  )
}

/** A vendor's per-region files, said once, opened on a click. */
function SourceFiles({ files }: { files: string[] }) {
  const [open, setOpen] = useState(false)
  return open
    ? <ul className="vsrc">{files.map((u) => <li key={u}><a href={u} target="_blank" rel="noreferrer nofollow">{u.replace(/^https?:\/\//, '')}</a></li>)}</ul>
    : <button type="button" className="linklike" onClick={() => setOpen(true)}>show them</button>
}

/** Where a vendor runs, in one place: one sentence, the visitor's country first (found in the browser, changed like
 *  every other page), every country with its regions and what is sold there; a global price with no stated place,
 *  and the vendor's own notes on where a request is processed. */
function WhereItRuns({ w, label }: { w: VendorWhere; label: string }) {
  const here = useCountry()
  const meta = useAsync(() => api.regionsMeta(), [])
  const names = meta.data?.countries ?? {}
  const [all, setAll] = useState(false)
  const mine = here.code ? w.countries.find((c) => c.code === here.code && !c.gov) : undefined
  const mineDoc = here.code ? w.documented.find((d) => d.code === here.code) : undefined
  const country = here.code ? (names[here.code]?.name ?? here.code) : null
  const rows = all ? w.countries : w.countries.slice(0, 8)
  const shownMine = mine && !rows.includes(mine) ? [...rows, mine] : rows
  return (
    <section className="panel explore" id="where">
      <h2>Where it runs</h2>
      <p style={{ marginTop: 0 }}><b>{w.summary}</b></p>
      <div className="row whererow">
        <span className={`locbadge ${here.phase === 'detecting' ? 'busy' : here.code ? 'ok' : ''}`} role="status" aria-live="polite"
          title="Worked out in your browser from your time zone. Nothing is sent anywhere.">
          <span className="sig" aria-hidden="true"><i /><b /><b /></span>
          <span className="txt">{here.phase === 'detecting' ? 'Detecting your location…' : country ? `${country} ${here.chosen ? '· your choice' : 'detected'}` : 'Location unknown · choose a country'}</span>
          <select className="locpick" value="" aria-label="Choose another country" onChange={(e) => e.target.value && here.choose(e.target.value)}>
            <option value="">Change</option>
            {Object.entries(names).map(([code, c]) => <option key={code} value={code}>{c.name}</option>)}
          </select>
          <button type="button" className="locagain" aria-label="Detect my location again" title="Detect again" onClick={here.detectAgain}>↻</button>
        </span>
        {country && here.phase !== 'detecting' ? (
          <span className="small">{label} there: {mine
            ? <><b>{mine.regions.join(', ')}</b> · per-token prices for {mine.models} model{mine.models === 1 ? '' : 's'}{mine.gpus ? ` · ${mine.gpus} kind${mine.gpus === 1 ? '' : 's'} of GPU` : ''}</>
            : mineDoc ? <><b>{mineDoc.regions.map((r) => r.name).join(', ')}</b> · {mineDoc.models} model{mineDoc.models === 1 ? '' : 's'} on demand, per Oracle's documentation</>
            : w.countries.length || w.documented.length ? <>no region in {country}</> : <>no stated place</>}</span>
        ) : null}
      </div>
      {w.countries.length ? (
        <div className="tablewrap">
          <table className="plain wherelist">
            <thead><tr><th>Country</th><th>Regions</th><th className="num">Per-token models</th><th className="num">GPU kinds</th></tr></thead>
            <tbody>{shownMine.map((c) => (
              <tr key={c.code + (c.gov ? '-gov' : '')} className={c === mine ? 'here' : undefined}>
                <td>{c === mine ? <b>{c.name}</b> : c.name}{c.gov ? <span className="tag plain" style={{ marginLeft: 6 }}>government</span> : null}</td>
                <td>{c.regions.map((r) => <code key={r}>{r}</code>)}</td>
                <td className="num">{c.models || '—'}</td><td className="num">{c.gpus || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
          {w.countries.length > 8 && !all ? <button type="button" className="linklike small" onClick={() => setAll(true)}>show every country</button> : null}
        </div>
      ) : null}
      {w.documented.length ? (
        <div className="tablewrap">
          <table className="plain wherelist">
            <thead><tr><th>Country</th><th>Region</th><th className="num">Models on demand</th></tr></thead>
            <tbody>{w.documented.map((d) => (
              <tr key={d.code || d.name} className={d === mineDoc ? 'here' : undefined}>
                <td>{d === mineDoc ? <b>{d.name}</b> : d.name}</td>
                <td>{d.regions.map((r) => r.name).join(' · ')}</td>
                <td className="num">{d.regions.map((r) => r.models).join(' · ')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      {w.unplaced.length ? (
        <p className="small"><b>No place stated</b> for {w.unplaced.length === 1 ? '1 model' : `${w.unplaced.length} models`}: one global price, and {label} does not say where a request is processed ({w.unplaced.slice(0, 4).join(', ')}{w.unplaced.length > 4 ? ', …' : ''}).</p>
      ) : null}
      {w.other_regions.length ? <p className="small muted">Also priced in {w.other_regions.join(', ')}, which this page cannot place in a country.</p> : null}
      {w.notes.map((n) => <p key={n} className="small muted">{label}: {n}</p>)}
      {w.gpu_note ? <p className="small muted">{w.gpu_note}</p> : null}
    </section>
  )
}

function Cloud({ d, go }: { d: CloudPageData; go: Go }) {
  const cheapest = [...d.models].sort((a, b) => (a.input ?? 0) * 3 + (a.output ?? 0) - ((b.input ?? 0) * 3 + (b.output ?? 0)))[0]
  return (
    <>
      <section className="panel explore">
        <Crumbs go={go} trail={[['vendors', 'Vendors']]} />
        <div className="vhead">
          <Logo src={d.profile?.logo} label={d.label} size={56} />
          <div className="vtitle">
            <h2 className="pagehead">{d.label}</h2>
            {d.profile?.kind?.length || d.sells?.length ? (
              <p className="vkinds">{[...(d.profile?.kind ?? []), ...(d.sells ?? []).map((m) => SELLS[m] ?? m)].map((k) => <Tag key={k}>{k}</Tag>)}</p>
            ) : null}
          </div>
          {d.profile?.website ? <a className="pill vsite" href={d.profile.website} target="_blank" rel="noreferrer nofollow">{d.profile.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')} ↗</a> : null}
        </div>
        {d.profile?.intro ? <p className="vintro">{d.profile.intro}</p> : null}
        <dl className="vfacts">
          {d.profile?.hq ? <div><dt>Headquarters</dt><dd>{d.profile.hq}</dd></div> : null}
          {d.sells?.length ? <div><dt>Sells</dt><dd>{d.sells.map((m) => SELLS[m] ?? m).join(' · ')}</dd></div> : null}
          {d.where ? <div><dt>Runs in</dt><dd>{d.where.short} · <a href="#where" onClick={(e) => { e.preventDefault(); document.getElementById('where')?.scrollIntoView({ behavior: 'smooth' }) }}>where</a></dd></div> : null}
          {d.verified ? <div><dt>Checked by hand</dt><dd title={d.verified.note}>{d.verified.checked_on}{d.verified.by ? ` · ${d.verified.by}` : ''}</dd></div> : null}
        </dl>
      </section>
      <section className="panel explore">
        <h2>Prices at a glance</h2>
        <div className="grid3 trafficstats">
          <Tile win label="Models sold per token" value={String(d.models.length)} foot={cheapest ? `from ${usd(cheapest.input)} / ${usd(cheapest.output)} · ${cheapest.label}` : undefined} />
          <Tile label="Kinds of GPU to rent" value={String(d.gpus.length)} foot={d.gpus[0] ? `from ${usd(d.gpus[0].usd_per_gpu_hour)} a GPU-hour · ${d.gpus[0].gpu}` : undefined} />
          {d.prices ? <Tile label="Prices held" value={d.prices.toLocaleString()} foot={d.collected_at ? `collected ${day(d.collected_at)}` : undefined} /> : null}
        </div>
      </section>
      {d.models.length ? (
        <section className="panel explore">
          <h2>Models sold per token</h2>
          <p className="small muted" style={{ marginTop: 0 }}>The cheapest price anywhere {d.label} sells each model, at its standard tier. Where each is sold: Where it runs, below.</p>
          <div className="tablewrap">
            <table className="plain">
              <thead><tr><th>Model</th><th>Input, per 1M</th><th>Output, per 1M</th></tr></thead>
              <tbody>{d.models.map((m) => (
                <tr key={m.slug}><td><A to={`models/${m.slug}`} go={go}>{m.label}</A></td><td>{usd(m.input)}</td><td>{usd(m.output)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      ) : null}
      {d.gpus.length ? (
        <section className="panel explore">
          <h2>GPUs for hosting your own</h2>
          <div className="tablewrap">
            <table className="plain">
              <thead><tr><th>GPU</th><th>From, per GPU-hour</th><th>Cheapest instance</th><th>GPUs in it</th></tr></thead>
              <tbody>{d.gpus.map((g) => (
                <tr key={g.gpu}><td><A to={`gpus/${g.gpu_slug}`} go={go}>{g.gpu}</A></td><td>{usd(g.usd_per_gpu_hour)}</td><td><code>{g.instance}</code></td>
                  <td>{g.gpu_count} × {g.gpu_mem_gb} GB</td></tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      ) : null}
      {d.where ? <WhereItRuns w={d.where} label={d.label} /> : null}
      {d.profile?.sources?.length ? (
        <section className="panel explore">
          <h2>Sources</h2>
          <ul className="small vsrc">
            {d.profile.sources.map((x) => <li key={x.url}><a href={x.url} target="_blank" rel="noreferrer nofollow">{x.label}</a></li>)}
          </ul>
          {d.sources?.length ? (
            <ul className="small vsrc">
              {d.sources.map((s) => (
                <li key={s.kind}>{s.kind}: {s.items.map((it, i) => (
                  <span key={it.label}>{i ? ' · ' : ''}{it.url ? <a href={it.url} target="_blank" rel="noreferrer nofollow">{it.label}</a> : it.label}
                    {it.files?.length ? <> {it.note} <SourceFiles files={it.files} /></> : null}</span>
                ))}</li>
              ))}
            </ul>
          ) : null}
          <p className="small muted" style={{ margin: '8px 0 0' }}>Everything on this page is read from the sources above at each refresh, every three hours{d.verified ? `; last checked by hand against ${d.label}'s own page on ${d.verified.checked_on}` : ''}. The logo is {d.label}'s own mark, shown to identify it.</p>
        </section>
      ) : null}
      <Collected at={d.collected_at} />
    </>
  )
}

function Gpus({ d, go }: { d: Extract<ExplorePageData, { kind: 'gpus' }>; go: Go }) {
  return (
    <section className="panel explore">
      <h2 className="pagehead">GPU prices per hour on every cloud</h2>
      <p className="muted">The on-demand list price of an hour of one GPU, on each cloud that rents it.</p>
      <div className="tablewrap">
        <table className="plain">
          <thead><tr><th>GPU</th><th>Memory</th><th>From, per GPU-hour</th><th>Cheapest on</th><th>Rented by</th></tr></thead>
          <tbody>{d.gpus.map((g) => (
            <tr key={g.slug}><td><A to={`gpus/${g.slug}`} go={go}><b>{g.label}</b></A></td><td>{g.mem_gb ? `${g.mem_gb} GB` : ''}</td>
              <td>{usd(g.from_usd_per_gpu_hour)}</td><td>{g.cheapest_cloud}</td><td className="muted">{g.clouds.join(', ')}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  )
}

function Gpu({ d, go }: { d: GpuPageData; go: Go }) {
  const best = d.rows[0]
  return (
    <>
      <section className="panel explore">
        <Crumbs go={go} trail={[['gpus', 'GPUs']]} />
        <h2 className="pagehead">{d.label}: price per hour on every cloud</h2>
        <div className="grid3 trafficstats">
          <Tile win label="From, per GPU-hour" value={usd(best?.usd_per_gpu_hour)} foot={best ? `${best.cloud} · ${best.instance}` : undefined} />
          <Tile label="Rented on demand by" value={`${d.clouds} cloud${d.clouds === 1 ? '' : 's'}`} foot={d.rows.map((r) => r.cloud).join(', ')} />
          {d.mem_gb ? <Tile label="Memory" value={`${d.mem_gb} GB`} foot="a GPU" /> : null}
        </div>
        <div className="tablewrap" style={{ marginTop: 14 }}>
          <table className="plain">
            <thead><tr><th>Cloud</th><th>Per GPU-hour</th><th>Cheapest instance</th><th>GPUs in it</th><th>Instance, an hour</th><th>Region</th><th>Instances with it</th></tr></thead>
            <tbody>{d.rows.map((r, i) => (
              <tr key={r.provider}><td><A to={`vendors/${r.provider}`} go={go}>{r.cloud}</A>{i === 0 && d.rows.length > 1 ? <> <Tag>cheapest</Tag></> : null}</td>
                <td>{usd(r.usd_per_gpu_hour)}</td><td><code>{r.instance}</code></td><td>{r.gpu_count} × {r.gpu_mem_gb} GB</td><td>{usd(r.usd_per_hour)}</td><td>{r.region}</td><td>{r.instances}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </section>
      {d.holds.length ? (
        <section className="panel explore">
          <h2>What one of them can run</h2>
          <p className="small">{d.holds.map((h, i) => <span key={h.slug}>{i ? ' · ' : ''}<A to={`models/${h.slug}`} go={go}>{h.label}</A> <span className="muted">({h.need_gb} GB)</span></span>)}</p>
        </section>
      ) : null}
      <Collected at={d.collected_at} />
    </>
  )
}

const GROUPS = ['Middle East', 'Europe', 'Americas', 'Asia-Pacific', 'Africa', 'Other']
/** a country's flag from its two-letter code (regional indicator letters); text, no image request */
const flag = (code: string) => code.length === 2 ? String.fromCodePoint(...[...code.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65)) : ''

/** Every country where a vendor has a region (catalog.country_index): grouped by region of the world, A-Z inside,
 *  a search, and the visitor's own country first. Nothing is ranked: the order is the reader's choice. */
function Countries({ d, go }: { d: Extract<ExplorePageData, { kind: 'countries' }>; go: Go }) {
  const [q, setQ] = useState('')
  const [order, setOrder] = useState<'region' | 'az' | 'clouds'>('region')
  const here = useCountry()
  const mine = d.countries.find((c) => c.code === here.code)
  const needle = q.trim().toLowerCase()
  const shown = d.countries.filter((c) => !needle || c.name.toLowerCase().includes(needle)
    || c.clouds.some((v) => v.toLowerCase().includes(needle)))
  const card = (c: (typeof d.countries)[number]) => (
    <A key={c.slug} to={`in-country/${c.slug}`} go={go} className="vndcard ctycard">
      <span className="flag" aria-hidden="true">{flag(c.code)}</span>
      <span className="vndcardbody">
        <b>{c.name}</b>
        <span className="muted small">{c.clouds.length} {c.clouds.length === 1 ? 'vendor' : 'vendors'} with a region there</span>
        <span className="ctyvendors">{c.clouds.join(' · ')}</span>
      </span>
    </A>
  )
  const sorted = order === 'clouds' ? [...shown].sort((a, b) => b.clouds.length - a.clouds.length || a.name.localeCompare(b.name)) : shown
  return (
    <section className="panel explore">
      <h2 className="pagehead">LLM hosting by country</h2>
      <p className="muted">Which vendors run language models inside each country, and what each charges there per token. A price
        here is for a region in the country: data sent to the model does not leave it. {d.countries.length} countries.</p>
      <div className="ctybar">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a country or a vendor" aria-label="Find a country or a vendor" />
        <span className="seg" role="group" aria-label="Order">
          <button type="button" aria-pressed={order === 'region'} onClick={() => setOrder('region')}>By region</button>
          <button type="button" aria-pressed={order === 'az'} onClick={() => setOrder('az')}>A–Z</button>
          <button type="button" aria-pressed={order === 'clouds'} onClick={() => setOrder('clouds')}>Most vendors</button>
        </span>
      </div>
      {mine && !needle ? (
        <A to={`in-country/${mine.slug}`} go={go} className="ctyhere">
          <span aria-hidden="true">{flag(mine.code)}</span> You seem to be in <b>{mine.name}</b> · {mine.clouds.length} {mine.clouds.length === 1 ? 'vendor' : 'vendors'} with a region there <span className="go">Open ›</span>
        </A>
      ) : null}
      {order === 'region' ? GROUPS.filter((g) => sorted.some((c) => c.group === g)).map((g) => (
        <div key={g}>
          <h3 className="ctygroup">{g}</h3>
          <div className="vndcards">{sorted.filter((c) => c.group === g).map(card)}</div>
        </div>
      )) : <div className="vndcards" style={{ marginTop: 12 }}>{sorted.map(card)}</div>}
      {!shown.length ? <p className="muted">No country or vendor matches “{q}”.</p> : null}
    </section>
  )
}

function Country({ d, go }: { d: CountryPageData; go: Go }) {
  const have = d.clouds.filter((c) => c.regions.length)
  const absent = d.clouds.filter((c) => !c.regions.length).map((c) => c.cloud)
  return (
    <>
      <section className="panel explore">
        <Crumbs go={go} trail={[['in-country', 'By country']]} />
        <h2 className="pagehead">LLM hosting inside {d.name}</h2>
        <p className="muted">Which clouds run language models inside {d.name}, which models each sells there per token, and at what price. A price here is for a region in the country: data sent to the model does not leave it.</p>
        <div className="grid3 cloudcards">
          {have.map((c) => (
            <A key={c.provider} to={`vendors/${c.provider}`} go={go} className="cloudcard"><b>{c.cloud}</b><span>{c.regions.join(', ')}</span></A>
          ))}
        </div>
        {absent.length ? <p className="muted small" style={{ marginTop: 10 }}>No region in the country: {absent.join(', ')}.</p> : null}
      </section>
      {d.models.length ? (
        <section className="panel explore">
          <h2>Models sold in the country, per token</h2>
          <div className="tablewrap">
            <table className="plain">
              <thead><tr><th>Model</th>{d.models[0].cells.map((c) => <th key={c.provider}>{c.cloud}</th>)}</tr></thead>
              <tbody>{d.models.map((m) => (
                <tr key={m.slug}><td><A to={`models/${m.slug}`} go={go}>{m.label}</A></td>
                  {m.cells.map((c) => <td key={c.provider}>{c.input != null || c.output != null ? `${usd(c.input)} / ${usd(c.output)}` : <span className="muted">—</span>}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
          <p className="muted small">US dollars per million tokens, input / output.</p>
        </section>
      ) : null}
      <Collected at={d.collected_at} />
    </>
  )
}

/** the feed's link, where the feed is on */
function Feed() {
  return useSettings().explore?.live.includes('feed.xml') === false ? null : <a href={`${BASE}feed.xml`}>RSS feed</a>
}

const signed = (pct: number) => `${pct > 0 ? '+' : '−'}${Math.abs(pct).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`

function Report({ d, go }: { d: ReportPageData; go: Go }) {
  const c = d.counts
  return (
    <>
      <section className="panel explore">
        <Crumbs go={go} trail={[['reports', 'Reports']]} />
        <h2 className="pagehead">LLM hosting prices: what changed in {d.label}</h2>
        <div className="row reportbar">
          {d.in_progress ? <span className="chip warn">Month in progress{d.updated_at ? ` · updated ${day(d.updated_at)}` : ''}</span> : <span className="chip">Complete</span>}
          <span className="spacer" />
          {d.previous ? <A to={`reports/${d.previous}`} go={go} className="pill">‹ The month before</A> : null}
          {d.next ? <A to={`reports/${d.next}`} go={go} className="pill">The month after ›</A> : null}
          {d.pdf ? <a className="pill" href={`${BASE}api/reports/${d.month}/pdf`}>Download PDF</a> : null}
        </div>
        <p className="reportsum">{d.summary}</p>
        <div className="grid3 trafficstats">
          <div className={c.price_cuts ? 'stat cut' : 'stat'}><div className="label">Price cuts</div><div className="value">{c.price_cuts}</div></div>
          <div className={c.price_rises ? 'stat rise' : 'stat'}><div className="label">Price rises</div><div className="value">{c.price_rises}</div></div>
          <Tile label="Models newly sold" value={String(c.models_sold)} />
          <Tile label="Withdrawn" value={String(c.models_withdrawn)} />
          <Tile label="New GPU instances" value={String(c.instances)} />
        </div>
      </section>

      {d.moves.length ? (
        <section className="panel explore">
          <h2>Biggest price moves</h2>
          <div className="tablewrap">
            <table className="plain">
              <thead><tr><th>Cloud</th><th>What</th><th>Price</th><th>Change</th></tr></thead>
              <tbody>{d.moves.map((m) => (
                <tr key={m.id}><td>{m.cloud}</td><td><A to={m.path} go={go}>{m.what}</A></td>
                  <td>{usd(m.old)} → {usd(m.new)} {m.unit}</td><td className={m.pct < 0 ? 'oktext' : 'badtext'}><b>{signed(m.pct)}</b></td></tr>
              ))}</tbody>
            </table>
          </div>
          {d.more_moves ? <p className="muted small">And {d.more_moves} more, on the <A to="changes" go={go}>list of changes</A>.</p> : null}
        </section>
      ) : null}

      {d.sold.length || d.withdrawn.length || d.retiring.length ? (
        <section className="panel explore">
          <div className="reqlists">
            <div>
              <h3>Models newly sold per token</h3>
              {d.sold.length ? (
                <ul className="reportlist">{d.sold.map((x) => (
                  <li key={`${x.cloud}-${x.name}`}><A to={x.path} go={go}>{x.cloud} · {x.name}</A>
                    {x.input != null ? <span className="muted"> from {usd(x.input)} in / {usd(x.output)} out</span> : null}</li>
                ))}</ul>
              ) : <p className="muted small">None this month.</p>}
            </div>
            <div>
              <h3>Withdrawn and retiring</h3>
              {d.withdrawn.length || d.retiring.length ? (
                <ul className="reportlist">
                  {d.withdrawn.map((x) => <li key={`${x.cloud}-${x.name}`}><A to={x.path} go={go}>{x.cloud} no longer sells {x.name}</A></li>)}
                  {d.retiring.map((r, i) => <li key={`r${i}`}>{r.cloud} retires {r.model}{r.on ? ` on ${r.on}` : ''}{r.replacement ? <span className="muted"> · replacement: {r.replacement}</span> : null}</li>)}
                </ul>
              ) : <p className="muted small">None this month.</p>}
            </div>
          </div>
        </section>
      ) : null}

      {d.instances.length || d.regions.length || d.announcements.length ? (
        <section className="panel explore">
          <h2>Also this month</h2>
          {d.instances.length ? <p className="small"><b>{c.instances} new GPU instances:</b> {d.instances.map((x) => `${x.cloud} ${x.count}`).join(' · ')}</p> : null}
          {d.regions.length ? <p className="small"><b>{c.regions} models or instances in new regions:</b> {d.regions.map((x) => `${x.cloud} ${x.count}`).join(' · ')}</p> : null}
          {d.announcements.length ? (
            <>
              <p className="small"><b>{d.announcements.length} announcement{d.announcements.length === 1 ? '' : 's'} from the clouds:</b></p>
              <ul className="reportlist">{d.announcements.map((a, i) => (
                <li key={i}>{a.cloud}: {a.url ? <a href={a.url} target="_blank" rel="noopener noreferrer">{a.title}</a> : a.title}</li>
              ))}</ul>
            </>
          ) : null}
        </section>
      ) : null}
      <p className="muted small collected">Found by comparing each collection of the clouds' public list prices with the one before. <A to="reports" go={go}>Every month</A> · <Feed /></p>
    </>
  )
}

function Reports({ d, go }: { d: Extract<ExplorePageData, { kind: 'reports' }>; go: Go }) {
  return (
    <section className="panel explore">
      <h2 className="pagehead">LLM hosting prices: monthly reports</h2>
      <p className="muted">What changed each month in what the clouds charge for running language models.</p>
      <div className="tablewrap">
        <table className="plain">
          <thead><tr><th>Month</th><th>Price cuts</th><th>Price rises</th><th>Models newly sold</th><th>Withdrawn</th></tr></thead>
          <tbody>{d.months.map((m) => (
            <tr key={m.month}><td><A to={`reports/${m.month}`} go={go}><b>{m.label}</b></A>{m.in_progress ? <> <Tag>in progress</Tag></> : null}</td>
              <td>{m.price_cuts}</td><td>{m.price_rises}</td><td>{m.models_sold}</td><td>{m.models_withdrawn}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <p className="small" style={{ marginTop: 12 }}><A to="changes" go={go}>Every notable change, one by one</A> · <Feed /></p>
    </section>
  )
}

function Changes({ d, go }: { d: Extract<ExplorePageData, { kind: 'changes' }>; go: Go }) {
  const months = useMemo(() => {
    const by = new Map<string, typeof d.changes>()
    for (const c of d.changes) by.set(c.month, [...(by.get(c.month) ?? []), c])
    return [...by.entries()]
  }, [d.changes])
  return (
    <section className="panel explore">
      <h2 className="pagehead">LLM hosting price changes, as they happen</h2>
      <p className="muted">Price cuts and rises, models a cloud starts or stops selling, and retirements. <Feed /></p>
      {months.map(([month, list]) => (
        <div className="makergroup" key={month}>
          <h3><A to={`reports/${month}`} go={go}>{new Date(`${month}-15T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</A> <span className="muted small">{list.length}</span></h3>
          <ul className="reportlist">{list.map((c) => (
            <li key={c.id}><span className="muted">{new Date(c.at * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span> <A to={c.path} go={go}>{c.headline}</A></li>
          ))}</ul>
        </div>
      ))}
      {d.changes.length ? null : <p className="muted small">No notable change has been recorded yet.</p>}
    </section>
  )
}

function Change({ d, go }: { d: ChangePageData; go: Go }) {
  const price = d.old != null && d.new != null
  const top = Math.max(1e-9, ...d.history.map((p) => p.price))
  return (
    <>
      <section className="panel explore">
        <p className="crumbs small"><A to="changes" go={go}>Changes</A> › {day(d.at)} · {d.cloud}</p>
        <h2 className="pagehead">{d.headline}</h2>
        <div className="grid3 trafficstats">
          {price ? (
            <>
              <Tile label="Before" value={`${usd(d.old)} ${d.unit}`} />
              <div className={d.pct != null && d.pct < 0 ? 'stat cut' : 'stat rise'}><div className="label">Now</div><div className="value">{usd(d.new)} {d.unit}</div>
                <div className="foot">{d.pct != null ? signed(d.pct) : ''}</div></div>
              <Tile label={d.gpu ? 'Instance' : 'What'} value={d.name} foot={[d.gpu, d.pricing_model && d.pricing_model !== 'on_demand' ? d.pricing_model : '', d.region].filter(Boolean).join(' · ')} />
            </>
          ) : d.what === 'model_added' ? (
            <>
              <Tile win label="Model" value={d.name} foot={`now sold per token by ${d.cloud}`} />
              {d.input != null ? <Tile label="Input, per 1M tokens" value={usd(d.input)} /> : null}
              {d.output != null ? <Tile label="Output, per 1M tokens" value={usd(d.output)} /> : null}
              {d.regions.length ? <Tile label="Sold in" value={d.regions.length === 1 ? d.regions[0] : `${d.regions.length} regions`} foot={d.regions.length > 1 ? d.regions.slice(0, 4).join(', ') : undefined} /> : null}
            </>
          ) : (
            <Tile label={d.what === 'retirement' ? 'Retiring' : 'Withdrawn'} value={d.name || d.cloud} foot={d.retires_at ? `on ${d.retires_at}` : `no longer sold per token by ${d.cloud}`} />
          )}
        </div>
        {d.history.length > 1 ? (
          <>
            <div className="evbars month" aria-label="This price over the stored collections" style={{ marginTop: 14 }}>
              {d.history.map((p) => <i key={p.at} className={p.price === d.new ? undefined : 'before'} title={`${day(p.at)}: ${usd(p.price)}`} style={{ height: `${Math.max(6, (p.price / top) * 100)}%` }} />)}
            </div>
            <p className="muted small">This price over the stored collections, {day(d.history[0].at)} to {day(d.history[d.history.length - 1].at)}.</p>
          </>
        ) : null}
        {d.means ? <p className="small"><b>What it means:</b> {d.means}.</p> : null}
        {d.replacement ? <p className="small">Replacement: {d.replacement}.</p> : null}
        {d.source ? <p className="small"><a href={d.source} target="_blank" rel="noopener noreferrer">The cloud's own notice</a></p> : null}
        <p className="small changelinks">{d.links.map((l, i) => <span key={l.path}>{i ? ' · ' : ''}<A to={l.path} go={go}>{l.label}</A></span>)}</p>
      </section>
      <p className="muted small collected">Found by comparing a collection of {d.cloud}'s public list prices with the one before.</p>
    </>
  )
}

export function ExplorePage({ path, go, openTool }: { path: string; go: Go; openTool: OpenTool }) {
  const page = useAsync<ExplorePageData>(() => api.explorePage(path), [path])
  const d = page.data
  // the browser tab and the description follow the page; a page that does not exist says so
  useEffect(() => {
    if (!d) return
    const dev = document.title.startsWith('DEV · ') ? 'DEV · ' : ''
    document.title = dev + d.title
    document.querySelector('meta[name="description"]')?.setAttribute('content', d.description)
    window.scrollTo(0, 0)
  }, [d])
  if (page.error) {
    return (
      <section className="panel explore">
        <h2 className="pagehead">Page not found</h2>
        <p className="muted">There is no page at this address. Try the list of <A to="models" go={go}>models</A>, <A to="vendors" go={go}>clouds</A>, <A to="gpus" go={go}>GPUs</A> or <A to="changes" go={go}>changes</A>.</p>
        <ErrorNote error={null} />
      </section>
    )
  }
  if (!d) return <section className="panel explore"><p className="muted small">loading…</p></section>
  switch (d.kind) {
    case 'model': return <Model d={d} go={go} openTool={openTool} />
    case 'models': return <Models d={d} go={go} />
    case 'family': return <Family d={d} go={go} />
    case 'clouds': return <Clouds d={d} go={go} />
    case 'cloud': return <Cloud d={d} go={go} />
    case 'gpus': return <Gpus d={d} go={go} />
    case 'gpu': return <Gpu d={d} go={go} />
    case 'country': return <Country d={d} go={go} />
    case 'countries': return <Countries d={d} go={go} />
    case 'report': return <Report d={d} go={go} />
    case 'reports': return <Reports d={d} go={go} />
    case 'change': return <Change d={d} go={go} />
    case 'changes': return <Changes d={d} go={go} />
  }
}
